// @flow
import { merge, isEmpty, filter, omit } from 'lodash'

import {
  QUERY_RUN_STARTED,
  QUERY_RUN_SUCCEEDED,
  QUERY_RUN_FAILED,
  QUERY_SET_QUALITY,
  QUERY_STATS_STARTED,
  QUERY_STATS_SUCCEEDED,
  QUERY_STATS_FAILED,
  STAT_FILTER_CHANGED
} from 'actions/search-actions'

import {
  SORT_TABLE,
  UPDATE_AGGREGATION,
  TIME_AGGREGATION_CHANGED,
  TIME_DIMENSION_CHANGED,
  VIEW_CHANGED,
  DIMENSION_CHANGED,
  DESCRIPTION_TOGGLE_EDIT,
  DESCRIPTION_TAG_FOR_EDIT,
  DESCRIPTION_SAVE_EDITS_STARTED,
  DESCRIPTION_SAVE_EDITS_SUCCEEDED,
  DESCRIPTION_SAVE_EDITS_FAILED,
  QC_DESCRIPTION_SAVE_EDITS_FAILED,
  QC_DESCRIPTION_SAVE_EDITS_STARTED,
  DESCRIPTION_BRAND_SUGGEST_SUCCEEDED,
  DESCRIPTION_CATEGORY_SUGGEST_SUCCEEDED,
  DESCRIPTION_BRAND_EDIT_SELECT,
  CHANGE_TOOLBAR_OPTION,
  DESCRIPTION_CATEGORY_EDIT_SELECT,
  START_LOADING_WHEEL,
  DESCRIPTION_EDIT_STATUS_UPDATED,
  CHANGE_SEARCH_COLUMNS_VISIBILITY,
  SEARCH_DESCRIPTION_SELECTION_CHANGED,
  BULK_STAGE_DICT_EDITS,
  BULK_UNSTAGE_DICT_EDITS,
  STAGE_DICT_EDIT,
  REMOVE_STAGED_EDIT,
  APPLY_TEMP_FILTER,
  REMOVE_TEMP_FILTER,
  WIDGETS_OPTIONS_CHANGED,
  FETCH_DESCRIPTION_EDIT_SUGGESTIONS,
  FETCH_DESCRIPTION_EDIT_SUGGESTIONS_SUCCEEDED,
  FETCH_DESCRIPTION_EDIT_SUGGESTIONS_FAILED,
  FETCH_QC_DESCRIPTION_EDIT_SUGGESTIONS_SUCCEEDED,
  DESCRIPTION_TRAIN_EDIT_SELECT
} from 'actions/widget-actions'

import { QC_MONITORING_GRAPHS_CLEANUP } from 'actions/qc-actions.js'
import { LOGOUT_SUCCESS } from 'actions/auth-actions'
import { searchFields } from 'constants/DescriptionTableConfig'
import constants from 'constants/constants'
import updateDescription from 'utils/qc-tool-description'

import { METRIC_STORE_KEY } from './../components/content/widgets/Metrics/constants'

const defaultEdits = {
  inEdit: false,
  trackedEdits: {},
  stagedEdits: {},
  tempFilters: {},
  edits: {
    rows: [],
    brandSuggestions: [],
    categorySuggestions: [],
    brandId: null,
    brandName: null,
    categoryId: null,
    categoryFullPath: null,
    train: null
  }
}

const defaultVisibleColumns = filter(searchFields, 'visible', 'false').map(field => field.value)
const initialSearchEditState = {
  [constants.aggregations.DESCRIPTION]: {
    ...defaultEdits,
    data: [],
    loading: false,
    showToolbar: false,
    selectedSize: 200,
    sortBy: '_count',
    sortOrder: 'desc',
    columns: defaultVisibleColumns
  },
  [constants.aggregations.BRAND]: {
    data: [],
    loading: false,
    selectedSize: 10,
    selectedView: 'table',
    selectedField: 'count',
    sortBy: '_count',
    sortOrder: 'desc',
    showToolbar: false
  },
  [constants.aggregations.MERCHANT]: {
    data: [],
    loading: false,
    selectedSize: 10,
    selectedView: 'table',
    selectedField: 'count',
    sortBy: '_count',
    sortOrder: 'desc',
    showToolbar: false
  },
  [constants.aggregations.CATEGORY]: {
    data: [],
    loading: false,
    selectedSize: 10,
    selectedView: 'table',
    selectedField: 'count',
    sortBy: '_count',
    sortOrder: 'desc',
    showToolbar: false
  },
  [constants.aggregations.DESCRIPTION_FIRST_WORD]: {
    data: [],
    loading: false,
    selectedSize: 10,
    selectedView: 'table',
    selectedField: 'count',
    sortBy: '_count',
    sortOrder: 'desc',
    showToolbar: false
  },
  [constants.aggregations.TIME]: {
    data: {},
    loading: false,
    selectedField: 'revenue',
    selectedFilter: 'merchant_name',
    selectedInterval: 'month'
  },
  [constants.aggregations.DICTIONARY_COVERAGE]: {
    data: {},
    loading: false
  },
  [METRIC_STORE_KEY.INCLUDE]: {
    data: {},
    loading: false
  },
  [METRIC_STORE_KEY.EXCLUDE]: {
    data: {},
    loading: false
  },
  [METRIC_STORE_KEY.MISBRAND]: {
    data: {},
    loading: false
  },
  [METRIC_STORE_KEY.INCLUDE_COVERAGE]: {
    data: {},
    loading: false
  },
  stats: { selectedFilter: 'count' }
}

const handleStatsQuery = (action, state) => {
  let updatedStatProp = {}
  if (action.type === 'QUERY_STATS_SUCCEEDED') {
    if (action.statType === 'descriptionStat') {
      updatedStatProp = {
        count: action.data[action.aggType].value,
        loading: action.loading
      }
    } else {
      updatedStatProp = {
        count: action.data[action.aggType].count,
        revenue: action.data[action.aggType].value,
        loading: action.loading
      }
    }
  } else {
    if (action.statType === 'descriptionStat') {
      updatedStatProp = {
        count: 0,
        loading: action.loading
      }
    } else {
      updatedStatProp = {
        count: 0,
        revenue: 0,
        loading: action.loading
      }
    }
  }
  return {
    ...state,
    stats: {
      ...state.stats,
      [action.aggType]: {
        ...updatedStatProp
      }
    }
  }
}

const searchEditReducer = (state: Object = initialSearchEditState, action: Object): Object => {
  switch (action.type) {
    case DESCRIPTION_BRAND_SUGGEST_SUCCEEDED:
      return {
        ...state,
        description: {
          ...state.description,
          edits: {
            ...state.description.edits,
            brandSuggestions: action.data
          }
        }
      }

    case DESCRIPTION_CATEGORY_SUGGEST_SUCCEEDED:
      return {
        ...state,
        description: {
          ...state.description,
          edits: {
            ...state.description.edits,
            categorySuggestions: action.data
          }
        }
      }

    case BULK_STAGE_DICT_EDITS:
      const { rows, brandId, brandName, categoryId, categoryFullPath, train } = state.description.edits
      const stagedEdits = { ...state.description.stagedEdits } || {}
      if (train != null) {
        rows.forEach(row => {
          stagedEdits[row.id] = stagedEdits[row.id] || {}
          stagedEdits[row.id].train = {
            id: train,
            name: Boolean(train).toString()
          }
        })
      }
      if (brandId) {
        rows.forEach(row => {
          if (row.isBrandEditable) {
            stagedEdits[row.id] = stagedEdits[row.id] || {}
            stagedEdits[row.id].brand = {
              id: brandId,
              name: brandName
            }
          }
        })
      }
      if (categoryId) {
        rows.forEach(row => {
          if (row.isCategoryEditable) {
            stagedEdits[row.id] = stagedEdits[row.id] || {}
            stagedEdits[row.id].category = {
              id: categoryId,
              name: categoryFullPath
            }
          }
        })
      }
      if (!brandId && !categoryId && train == null) {
        rows.forEach(row => {
          stagedEdits[row.id] = stagedEdits[row.id] || {}
          if (row.isBrandEditable) {
            stagedEdits[row.id].brand = {
              id: row.brand_id,
              name: row.brand_full_path
            }
          }
          if (row.isCategoryEditable) {
            stagedEdits[row.id].category = {
              id: row.category_id,
              name: row.category_full_path
            }
          }
        })
      }
      return {
        ...state,
        description: {
          ...state.description,
          ...defaultEdits,
          stagedEdits: { ...stagedEdits },
          trackedEdits: state.description.trackedEdits
        }
      }
    case BULK_UNSTAGE_DICT_EDITS:
      return {
        ...state,
        description: {
          ...state.description,
          stagedEdits: {}
        }
      }
    case DESCRIPTION_BRAND_EDIT_SELECT:
      return {
        ...state,
        description: {
          ...state.description,
          edits: {
            ...state.description.edits,
            brandId: action.data ? action.data.id : null,
            brandName: action.data ? action.data.value : null
          }
        }
      }

    case DESCRIPTION_CATEGORY_EDIT_SELECT:
      return {
        ...state,
        description: {
          ...state.description,
          edits: {
            ...state.description.edits,
            categoryId: action.data ? action.data.id : null,
            categoryFullPath: action.data ? action.data.value : null,
            train: action.data ? true : null
          }
        }
      }

    case DESCRIPTION_TRAIN_EDIT_SELECT:
      return {
        ...state,
        description: {
          ...state.description,
          edits: {
            ...state.description.edits,
            train: action.data?.value
          }
        }
      }

    case DESCRIPTION_SAVE_EDITS_STARTED:
    case QC_DESCRIPTION_SAVE_EDITS_STARTED:
      return {
        ...state,
        description: {
          ...state.description,
          loading: true
        }
      }
    case QC_DESCRIPTION_SAVE_EDITS_FAILED:
    case DESCRIPTION_SAVE_EDITS_FAILED:
      return {
        ...state,
        description: {
          ...state.description,
          loading: false
        }
      }
    case DESCRIPTION_SAVE_EDITS_SUCCEEDED:
      let newStagedEdits = { ...state.description.stagedEdits }
      if (action.removeKeys.length) {
        newStagedEdits = omit(newStagedEdits, action.removeKeys)
      }
      return {
        ...state,
        description: {
          ...state.description,
          ...defaultEdits,
          stagedEdits: newStagedEdits,
          tempFilters: { ...state.description.tempFilters },
          trackedEdits: { ...state.description.trackedEdits },
          loading: false
        }
      }

    case DESCRIPTION_TAG_FOR_EDIT:
      return {
        ...state,
        description: {
          ...state.description,
          edits: {
            ...state.description.edits,
            rows: action.data.add
              ? state.description.edits.rows.concat(action.data.rows)
              : state.description.edits.rows.filter(
                  row => action.data.rows.findIndex(dataRow => dataRow.id === row.id) === -1
                )
          }
        }
      }

    case DESCRIPTION_TOGGLE_EDIT:
      let edits = {
        ...state.description.edits
      }
      if (state.description.inEdit) {
        edits = {
          ...edits,
          rows: [],
          brandSuggestions: [],
          categorySuggestions: [],
          brandId: null,
          brandName: null,
          categoryId: null,
          categoryFullPath: null
        }
      }
      return {
        ...state,
        description: {
          ...state.description,
          inEdit: !state.description.inEdit,
          edits
        }
      }

    case STAGE_DICT_EDIT:
      return {
        ...state,
        description: {
          ...state.description,
          stagedEdits: {
            ...state.description.stagedEdits,
            [action.key]: {
              ...state.description.stagedEdits[action.key],
              [action.attribute]: {
                id: action.id,
                name: action.name
              }
            }
          }
        }
      }

    case REMOVE_STAGED_EDIT:
      const updatedStagedEdits = state.description.stagedEdits
      delete updatedStagedEdits[action.key][action.attribute]
      if (isEmpty(updatedStagedEdits[action.key])) {
        delete updatedStagedEdits[action.key]
      }
      return {
        ...state,
        description: {
          ...state.description,
          stagedEdits: { ...updatedStagedEdits }
        }
      }

    case UPDATE_AGGREGATION:
      return {
        ...state,
        [action.data.aggType]: {
          ...state[action.data.aggType],
          ...action.data.aggregationUpdates
        }
      }

    case SEARCH_DESCRIPTION_SELECTION_CHANGED:
      return {
        ...state,
        description: {
          ...state.description,
          [action.attribute]: action.value
        }
      }

    case SORT_TABLE:
      return {
        ...state,
        [action.data.aggType]: {
          ...state[action.data.aggType],
          sortBy: action.data.sortBy,
          sortOrder: action.data.sortOrder
        }
      }

    case TIME_AGGREGATION_CHANGED:
      return {
        ...state,
        time: {
          ...state[constants.aggregations.TIME],
          selectedFilter: action.data.selectedFilter,
          selectedInterval: action.data.selectedInterval
        }
      }

    case TIME_DIMENSION_CHANGED:
      return {
        ...state,
        time: {
          ...state[constants.aggregations.TIME],
          selectedField: action.data.axis
        }
      }

    case DIMENSION_CHANGED:
      return {
        ...state,
        [action.data.aggType]: {
          ...state[action.data.aggType],
          selectedField: action.data.axis
        }
      }

    case VIEW_CHANGED:
      return {
        ...state,
        [action.data.aggType]: {
          ...state[action.data.aggType],
          selectedView: action.data.view
        }
      }

    case QUERY_STATS_STARTED:
      return handleStatsQuery(action, state)

    case QUERY_STATS_SUCCEEDED:
      return handleStatsQuery(action, state)

    case QUERY_STATS_FAILED:
      return handleStatsQuery(action, state)

    case START_LOADING_WHEEL:
      return {
        ...state,
        [action.aggType]: { ...state[action.aggType], loading: true }
      }

    case QUERY_RUN_STARTED:
      let aggType = { ...state[action.aggType], loading: true }

      if (!action.isTemp) {
        const emptyDataAggregations = [constants.aggregations.TIME, constants.aggregations.DICTIONARY_COVERAGE]
        aggType.data = emptyDataAggregations.includes(action.aggType) ? {} : []
      }

      if (action.aggType === constants.aggregations.DESCRIPTION) {
        aggType = { ...aggType, ...defaultEdits }
        if (action.isTemp) {
          aggType.tempFilters = state.description.tempFilters
          aggType.stagedEdits = state.description.stagedEdits
          aggType.trackedEdits = state.description.trackedEdits
        }
      }

      return { ...state, [action.aggType]: aggType }

    case QUERY_RUN_SUCCEEDED:
      return {
        ...state,
        [action.aggType]: {
          ...state[action.aggType],
          data: action.data[action.aggType] || {},
          hits: action.isTemp ? state[constants.aggregations.DESCRIPTION].hits : action.data.hits,
          loading: action.loading
        }
      }

    case QUERY_RUN_FAILED:
      return {
        ...state,
        [action.aggType]: {
          ...state[action.aggType],
          loading: action.loading
        },
        errorMessage: 'Query Failed'
      }

    case QUERY_SET_QUALITY:
      return {
        ...state,
        [action.aggType]: {
          ...state[action.aggType],
          isQueryCorrect: action.isQueryCorrect
        }
      }

    case DESCRIPTION_EDIT_STATUS_UPDATED:
      let descriptionData = state.description.data

      // updating the descriptionData with new values if necessary
      if (descriptionData && descriptionData.length) {
        for (const key in action.data) {
          if (action.data.hasOwnProperty(key)) {
            const value = action.data[key]
            // update brand values
            descriptionData = updateDescription(descriptionData, key, value.brand, 'brand_id', 'brand_name')
            // update category values
            descriptionData = updateDescription(
              descriptionData,
              key,
              value.category,
              'category_id',
              'category_full_path'
            )
            descriptionData = updateDescription(descriptionData, key, value.train, 'train', null)
          }
        }
      }
      return {
        ...state,
        description: {
          ...state.description,
          trackedEdits: merge({}, state.description.trackedEdits, action.data),
          data: descriptionData
        }
      }

    case CHANGE_TOOLBAR_OPTION:
      return {
        ...state,
        description: {
          ...state.description,
          showToolbar: !state.description.showToolbar
        }
      }

    case QC_MONITORING_GRAPHS_CLEANUP:
      return {
        ...state,
        [constants.aggregations.DICTIONARY_COVERAGE]: {
          data: {},
          loading: false
        },
        [METRIC_STORE_KEY.INCLUDE]: {
          data: {},
          loading: false
        },
        [METRIC_STORE_KEY.EXCLUDE]: {
          data: {},
          loading: false
        },
        [METRIC_STORE_KEY.MISBRAND]: {
          data: {},
          loading: false
        },
        [METRIC_STORE_KEY.INCLUDE_COVERAGE]: {
          data: {},
          loading: false
        }
      }

    case APPLY_TEMP_FILTER:
      return {
        ...state,
        description: {
          ...state.description,
          tempFilters: {
            ...state.description.tempFilters,
            [action.aggType]: action.value
          }
        }
      }

    case CHANGE_SEARCH_COLUMNS_VISIBILITY:
      const columns = state.description.columns
      const index = columns.indexOf(action.column)
      if (index === -1) {
        columns.push(action.column)
      } else {
        columns.splice(index, 1)
      }
      return {
        ...state,
        description: {
          ...state.description,
          columns: [...columns]
        }
      }

    case REMOVE_TEMP_FILTER:
      const tempFilters = { ...state.description.tempFilters }
      delete tempFilters[action.aggType]
      return {
        ...state,
        description: {
          ...state.description,
          tempFilters
        }
      }

    case WIDGETS_OPTIONS_CHANGED:
      return {
        ...state,
        [action.aggType]: {
          ...state[action.aggType],
          [action.attribute]: action.value
        }
      }

    case STAT_FILTER_CHANGED:
      return {
        ...state,
        stats: {
          ...state.stats,
          selectedFilter: action.selectedFilter
        }
      }

    case LOGOUT_SUCCESS:
      return initialSearchEditState

    case FETCH_DESCRIPTION_EDIT_SUGGESTIONS:
      return {
        ...state,
        description: {
          ...state.description,
          loading: true
        }
      }

    case FETCH_QC_DESCRIPTION_EDIT_SUGGESTIONS_SUCCEEDED:
    case FETCH_DESCRIPTION_EDIT_SUGGESTIONS_SUCCEEDED: {
      const stagedEdits = { ...state.description.stagedEdits } || {}
      const { data = {}, loading } = action
      Object.keys(data).forEach(descriptionId => {
        stagedEdits[descriptionId] = stagedEdits[descriptionId] || {}
        stagedEdits[descriptionId].brand = {
          id: data[descriptionId].brandId,
          name: data[descriptionId].brandName
        }
      })
      return {
        ...state,
        description: {
          ...state.description,
          ...defaultEdits,
          stagedEdits: { ...stagedEdits },
          trackedEdits: state.description.trackedEdits,
          loading
        }
      }
    }

    case FETCH_DESCRIPTION_EDIT_SUGGESTIONS_FAILED:
      return {
        ...state,
        description: {
          ...state.description,
          loading: false
        }
      }

    default: {
      return state
    }
  }
}

export default searchEditReducer
